import React, { useState, useContext } from "react";

import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import ForwardMessageModal from "../ForwardMessageModal";
import { Menu } from "@mui/material";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "👏", "🔥"];

const EDIT_LIMIT_MS = 15 * 60 * 1000;

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl }) => {
  const canEdit = message.fromMe && !message.isDeleted &&
    message.createdAt && (Date.now() - new Date(message.createdAt).getTime() < EDIT_LIMIT_MS);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const myReaction = (() => {
    if (!message.reactions || !user?.id) return null;
    const key = `agent_${user.id}`;
    for (const [emoji, senders] of Object.entries(message.reactions)) {
      if (Array.isArray(senders) && senders.includes(key)) return emoji;
    }
    return null;
  })();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState("");

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const hanldeReplyMessage = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const handleOpenForwardModal = () => {
    setForwardOpen(true);
    handleClose();
  };

  const handleStarMessage = async () => {
    handleClose();
    try {
      await api.post(`/messages/${message.id}/star`, { star: true });
      toast.success("Mensagem favoritada no WhatsApp ⭐");
    } catch (err) {
      toastError(err);
    }
  };

  const handleReact = async (emoji) => {
    handleClose();
    try {
      // clicar no mesmo emoji remove a reação
      const emojiToSend = emoji === myReaction ? "" : emoji;
      await api.post(`/messages/${message.id}/react`, { emoji: emojiToSend });
    } catch (err) {
      toastError(err);
    }
  };

  const handleMarkUnseen = async () => {
    handleClose();
    try {
      await api.post(`/messages/${message.id}/mark-unseen`);
      toast.success("Mensagem marcada como não lida.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenEditModal = () => {
    setEditBody(message.body || "");
    setEditOpen(true);
    handleClose();
  };

  const handleEditMessage = async () => {
    if (!editBody.trim()) return;
    try {
      await api.put(`/messages/${message.id}`, { body: editBody.trim() });
      toast.success("Mensagem editada com sucesso.");
      setEditOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <ForwardMessageModal
        open={forwardOpen}
        onClose={() => setForwardOpen(false)}
        messageId={message.id}
      />
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar mensagem</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Texto da mensagem"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleEditMessage();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="default">Cancelar</Button>
          <Button onClick={handleEditMessage} color="primary" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={menuOpen}
        onClose={handleClose}
      >
        {/* Barra de emojis rápidos */}
        {!message.isDeleted && (
          <div style={{ display: "flex", gap: 2, padding: "4px 8px", justifyContent: "center" }}>
            {QUICK_EMOJIS.map((emoji) => (
              <Tooltip key={emoji} title={emoji} placement="top">
                <span
                  onClick={() => handleReact(emoji)}
                  style={{
                    fontSize: 22,
                    cursor: "pointer",
                    padding: "4px 5px",
                    borderRadius: 8,
                    lineHeight: 1,
                    background: myReaction === emoji ? "#f0f4ff" : "transparent",
                    border: myReaction === emoji ? "2px solid #1976d2" : "2px solid transparent",
                    transition: "background 0.15s",
                    userSelect: "none",
                  }}
                  role="button"
                >
                  {emoji}
                </span>
              </Tooltip>
            ))}
          </div>
        )}
        {!message.isDeleted && <Divider style={{ margin: "2px 0 4px" }} />}
        {canEdit && (
          <MenuItem onClick={handleOpenEditModal}>
            Editar mensagem
          </MenuItem>
        )}
        {message.fromMe && (
          <MenuItem onClick={handleOpenConfirmationModal}>
            {i18n.t("messageOptionsMenu.delete")}
          </MenuItem>
        )}
        <MenuItem onClick={hanldeReplyMessage}>
          {i18n.t("messageOptionsMenu.reply")}
        </MenuItem>
        {!message.isDeleted && (
          <MenuItem onClick={handleOpenForwardModal}>
            Encaminhar
          </MenuItem>
        )}
        {!message.isDeleted && (
          <MenuItem onClick={handleStarMessage}>
            Favoritar ⭐
          </MenuItem>
        )}
        {!message.isDeleted && !message.fromMe && (
          <MenuItem onClick={handleMarkUnseen}>
            Marcar como não lido
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
